import { v4 as uuidv4 } from "uuid";
import React, { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { useIntl } from "react-intl";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Table,
} from "reactstrap";
import { useFacebookCore } from "../../cores/facebook";
import { useInstagramCore } from "../../cores/instagram";
import { useLinkedInCore } from "../../cores/linkedIn";
import { useLinkedInBusinessCore } from "../../cores/linkedInBusiness";
import { useMeCore } from "../../cores/me";
import { useRouterCore } from "../../cores/router";
import { Mentions } from "../../cores/socialPost/interfaces/Mention";
import { PostImage } from "../../cores/socialPost/interfaces/postImage";
import { PostVideo } from "../../cores/socialPost/interfaces/postVideo";
import { useTiktokCore } from "../../cores/tiktok";
import { isTiktokPostingEnabled } from "../../cores/config/featureFlags";
import { useTwitterCore } from "../../cores/twitter";
import { isInstagramSocialConnected } from "../../cores/socialPost/config";
import Content from "../layout/Content";
import { Header2 } from "../layout/Header2";
import Layout from "../layout/Layout";
import {
  ContentGridRow,
  consumeQueuedContentGridRows,
  createEmptyContentGridRow,
  isContentGridRowBlank,
  loadPersistedContentGridRows,
  persistContentGridRows,
  serializeContentGrid,
} from "../utils/socialContentGridShare";

const createEmptyRow = (): ContentGridRow => createEmptyContentGridRow();

export default function SocialContentGrid() {
  const intl = useIntl();
  const { routes, goToSocialManager } = useRouterCore();
  const { company, socialSettings } = useMeCore();
  const { socialPost: socialPostFacebook, socialPostVideo: socialPostVideoFacebook } =
    useFacebookCore();
  const {
    socialPost: socialPostInstagram,
    socialPostVideo: socialPostVideoInstagram,
  } = useInstagramCore();
  const { socialPost: socialPostLinkedIn } = useLinkedInCore();
  const {
    socialPost: socialPostLinkedInBusiness,
    socialPostVideo: socialPostVideoLinkedInBusiness,
  } = useLinkedInBusinessCore();
  const { socialPost: socialPostTwitter, socialPostVideo: socialPostVideoTwitter } =
    useTwitterCore();
  const { socialPost: socialPostTiktok, socialPostVideo: socialPostVideoTiktok } =
    useTiktokCore();
  const [isPlatformColumnPinned, setIsPlatformColumnPinned] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const pendingConsumedRef = useRef(false);
  const [rows, setRows] = useState<ContentGridRow[]>([createEmptyRow()]);
  const [selectedRowIds, setSelectedRowIds] = useState<string[]>([]);
  const [calendarModal, setCalendarModal] = useState<{
    mode: "single" | "bulk";
    rowId?: string;
  } | null>(null);
  const [calendarNetwork, setCalendarNetwork] = useState("instagram");
  const [calendarConfirmModal, setCalendarConfirmModal] = useState<{
    targetIds: string[];
    network: string;
  } | null>(null);
  const [isSendingCalendar, setIsSendingCalendar] = useState(false);
  const [calendarResultMessage, setCalendarResultMessage] = useState<string | null>(
    null
  );
  const [monthFilter, setMonthFilter] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "cards">("grid");
  const [publicLinkMessage, setPublicLinkMessage] = useState<{
    type: "success" | "danger";
    text: string;
  } | null>(null);
  const [inboxMessage, setInboxMessage] = useState<string | null>(null);

  const hasRows = rows.length > 0;
  const availableCalendarNetworks = useMemo(() => {
    const settings = (socialSettings ?? {}) as Record<string, unknown>;
    const options: Array<{ value: string; label: string }> = [];

    if (isInstagramSocialConnected(settings)) {
      options.push({ value: "instagram", label: "Instagram" });
    }
    if (settings.facebook) options.push({ value: "facebook", label: "Facebook" });
    if (settings.linkedIn) options.push({ value: "linkedin", label: "LinkedIn" });
    if (settings.linkedInBusiness) {
      options.push({ value: "linkedinBusiness", label: "LinkedIn Business" });
    }
    if (isTiktokPostingEnabled() && settings.tiktok) {
      options.push({ value: "tiktok", label: "TikTok" });
    }
    if (settings.twitter) options.push({ value: "x", label: "X" });

    return options;
  }, [socialSettings]);

  useEffect(() => {
    const pending = pendingConsumedRef.current
      ? []
      : consumeQueuedContentGridRows();
    pendingConsumedRef.current = true;
    const persisted = loadPersistedContentGridRows(company?.id) ?? [];

    if (pending.length > 0) {
      const base = persisted.filter((row) => !isContentGridRowBlank(row));
      const next = [...pending, ...base];
      setRows(next);
      persistContentGridRows(company?.id, next);
      setInboxMessage(
        intl.formatMessage(
          {
            id: "app.socialContentGrid.receivedFromComposer",
            defaultMessage:
              "Se agregaron {count} publicaciones desde el posteador.",
          },
          { count: pending.length }
        )
      );
    } else if (persisted.length > 0) {
      setRows(persisted);
    } else if (!hydrated) {
      setRows([createEmptyRow(), createEmptyRow()]);
    }
    setHydrated(true);
  }, [company?.id, intl, hydrated]);

  useEffect(() => {
    if (!hydrated) return;
    persistContentGridRows(company?.id, rows);
  }, [company?.id, rows, hydrated]);

  useEffect(() => {
    if (
      availableCalendarNetworks.length > 0 &&
      !availableCalendarNetworks.some((item) => item.value === calendarNetwork)
    ) {
      setCalendarNetwork(availableCalendarNetworks[0].value);
    }
    if (availableCalendarNetworks.length === 0 && calendarNetwork !== "") {
      setCalendarNetwork("");
    }
  }, [availableCalendarNetworks, calendarNetwork]);

  const getNetworkLabel = (networkValue: string) =>
    availableCalendarNetworks.find((item) => item.value === networkValue)?.label ??
    networkValue;

  const filteredRows = useMemo(() => {
    if (!monthFilter) return rows;
    return rows.filter((row) => row.date?.startsWith(monthFilter));
  }, [rows, monthFilter]);
  const filteredRowIds = useMemo(
    () => new Set(filteredRows.map((row) => row.id)),
    [filteredRows]
  );
  const selectedVisibleRowIds = useMemo(
    () => selectedRowIds.filter((id) => filteredRowIds.has(id)),
    [selectedRowIds, filteredRowIds]
  );

  const onChangeRow = (
    id: string,
    field: keyof Omit<ContentGridRow, "id">,
    value: string
  ) => {
    setRows((prev) =>
      prev.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const renderMediaPreview = (row: ContentGridRow) => {
    if (!row.mediaUrl) return null;

    if (row.mediaType === "image") {
      return (
        <img
          src={row.mediaUrl}
          alt="media-preview"
          style={{ maxWidth: 120, maxHeight: 120, objectFit: "cover" }}
          className="mt-2 rounded"
        />
      );
    }

    if (row.mediaType === "video") {
      return (
        <video
          src={row.mediaUrl}
          controls
          style={{ maxWidth: 140, maxHeight: 120 }}
          className="mt-2 rounded"
        />
      );
    }

    if (row.mediaType === "link") {
      return (
        <a
          href={row.mediaUrl}
          target="_blank"
          rel="noreferrer"
          className="d-block mt-2"
        >
          {intl.formatMessage({ id: "app.socialContentGrid.openLink" })}
        </a>
      );
    }

    return null;
  };

  const onUploadMedia = async (
    id: string,
    file: File,
    mediaType: ContentGridRow["mediaType"]
  ) => {
    const toDataUrl = (targetFile: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result ?? ""));
        reader.onerror = reject;
        reader.readAsDataURL(targetFile);
      });

    const mediaUrl = await toDataUrl(file);
    setRows((prev) =>
      prev.map((row) =>
        row.id === id ? { ...row, mediaType, mediaUrl } : row
      )
    );
  };

  const addRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((row) => row.id !== id));
    setSelectedRowIds((prev) => prev.filter((rowId) => rowId !== id));
  };

  const toggleRowSelection = (rowId: string) => {
    setSelectedRowIds((prev) =>
      prev.includes(rowId) ? prev.filter((id) => id !== rowId) : [...prev, rowId]
    );
  };

  const openSingleCalendarModal = (rowId: string) => {
    setCalendarModal({ mode: "single", rowId });
  };

  const openBulkCalendarModal = () => {
    if (selectedVisibleRowIds.length === 0) return;
    setCalendarModal({ mode: "bulk" });
  };

  const getTargetIdsFromModal = () => {
    if (!calendarModal) return [] as string[];
    return calendarModal.mode === "single" && calendarModal.rowId
      ? [calendarModal.rowId]
      : selectedVisibleRowIds;
  };

  const buildPostImage = (row: ContentGridRow): PostImage[] => {
    if (row.mediaType !== "image" || !row.mediaUrl) return [];
    return [
      {
        id: uuidv4(),
        lastModified: Date.now(),
        name: "grid-image.jpg",
        size: row.mediaUrl.length,
        type: "image/jpeg",
        data: row.mediaUrl,
      },
    ];
  };

  const buildPostVideo = (row: ContentGridRow): PostVideo | null => {
    if (row.mediaType !== "video" || !row.mediaUrl) return null;
    return {
      id: uuidv4(),
      lastModified: Date.now(),
      name: "grid-video.mp4",
      size: row.mediaUrl.length,
      type: "video/mp4",
      data: row.mediaUrl,
      duration: 0,
    };
  };

  const sendRowsToCalendar = async (targetRows: ContentGridRow[], network: string) => {
    if (!company?.id || targetRows.length === 0) return;
    const mentions: Mentions[] = [];
    const repostId = "";

    for (const row of targetRows) {
      const scheduleDate = row.date
        ? moment(`${row.date} 12:00`).toString()
        : moment().add(10, "minutes").toString();
      const withLinkText =
        row.mediaType === "link" && row.mediaUrl
          ? `${row.copy?.trim() ?? ""}\n${row.mediaUrl}`.trim()
          : row.copy ?? "";
      const images = buildPostImage(row);
      const video = buildPostVideo(row);

      switch (network) {
        case "instagram":
          if (video) {
            await socialPostVideoInstagram(
              company.id,
              withLinkText,
              mentions,
              video,
              scheduleDate,
              repostId
            );
          } else {
            await socialPostInstagram(
              company.id,
              withLinkText,
              mentions,
              images,
              scheduleDate,
              repostId
            );
          }
          break;
        case "facebook":
          if (video) {
            await socialPostVideoFacebook(
              company.id,
              withLinkText,
              mentions,
              video,
              scheduleDate,
              repostId
            );
          } else {
            await socialPostFacebook(
              company.id,
              withLinkText,
              mentions,
              images,
              scheduleDate,
              repostId
            );
          }
          break;
        case "linkedin":
          await socialPostLinkedIn(
            company.id,
            withLinkText,
            mentions,
            images,
            scheduleDate,
            repostId
          );
          break;
        case "linkedinBusiness":
          if (video) {
            await socialPostVideoLinkedInBusiness(
              company.id,
              withLinkText,
              mentions,
              video,
              scheduleDate,
              repostId
            );
          } else {
            await socialPostLinkedInBusiness(
              company.id,
              withLinkText,
              mentions,
              images,
              scheduleDate,
              repostId
            );
          }
          break;
        case "tiktok":
          if (video) {
            await socialPostVideoTiktok(
              company.id,
              withLinkText,
              mentions,
              video,
              scheduleDate,
              repostId
            );
          } else {
            await socialPostTiktok(
              company.id,
              withLinkText,
              mentions,
              images,
              scheduleDate,
              repostId
            );
          }
          break;
        case "x":
          if (video) {
            await socialPostVideoTwitter(
              company.id,
              withLinkText,
              mentions,
              video,
              scheduleDate,
              repostId
            );
          } else {
            await socialPostTwitter(
              company.id,
              withLinkText,
              mentions,
              images,
              scheduleDate,
              repostId
            );
          }
          break;
        default:
          break;
      }
    }
  };

  const confirmSendToCalendar = async () => {
    if (!calendarConfirmModal) return;
    if (!company?.id) {
      setCalendarResultMessage(
        intl.formatMessage({
          id: "app.socialContentGrid.calendarSendMissingCompany",
          defaultMessage: "No se encontro la compania activa para programar publicaciones.",
        })
      );
      setCalendarConfirmModal(null);
      return;
    }
    const { targetIds, network } = calendarConfirmModal;
    const targetRows = rows.filter((row) => targetIds.includes(row.id));
    if (targetRows.length === 0) {
      setCalendarConfirmModal(null);
      return;
    }

    setIsSendingCalendar(true);
    await sendRowsToCalendar(targetRows, network);

    setRows((prev) =>
      prev.map((row) =>
        targetIds.includes(row.id)
          ? {
              ...row,
              scheduledToCalendar: true,
              scheduledNetwork: network,
            }
          : row
      )
    );

    if (calendarModal?.mode === "bulk") {
      setSelectedRowIds([]);
    }

    setCalendarResultMessage(
      intl.formatMessage(
        {
          id: "app.socialContentGrid.calendarSendSuccess",
          defaultMessage: "Se enviaron {count} publicaciones al calendario.",
        },
        { count: targetRows.length }
      )
    );
    setIsSendingCalendar(false);
    setCalendarConfirmModal(null);
    setCalendarModal(null);
  };

  const generatedLink = useMemo(() => {
    if (!hasRows) return "";

    const payload = serializeContentGrid(rows);
    const basePath = routes.publicContentGrid;
    return `${window.location.origin}${basePath}?data=${payload}`;
  }, [hasRows, rows, routes.publicContentGrid]);

  const copyPublicLink = async () => {
    if (!generatedLink) return;

    try {
      await navigator.clipboard.writeText(generatedLink);
      setPublicLinkMessage({
        type: "success",
        text: intl.formatMessage({
          id: "app.socialContentGrid.publicLinkCopied",
          defaultMessage: "Link publico generado y copiado correctamente.",
        }),
      });
    } catch (error) {
      setPublicLinkMessage({
        type: "danger",
        text: intl.formatMessage({
          id: "app.socialContentGrid.publicLinkCopyError",
          defaultMessage:
            "No se pudo copiar automaticamente. Intenta nuevamente.",
        }),
      });
    }
  };

  return (
    <Layout>
      <Header2
        className="social-grid-header"
        title={intl.formatMessage({ id: "app.sidebar.socialContentGrid" })}
      />
      <Content>
        <Container fluid className="social-content-grid-page">
          {inboxMessage ? (
            <Row>
              <Col xs="12">
                <div className="alert alert-success py-2" role="status">
                  {inboxMessage}
                  <button
                    type="button"
                    className="close"
                    aria-label="Close"
                    onClick={() => setInboxMessage(null)}
                  >
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </Col>
            </Row>
          ) : null}
          <Row>
            <Col xs="12">
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="social-grid-toolbar d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div className="social-grid-toolbar-actions d-flex flex-wrap gap-2 align-items-center">
                    <Button color="primary" onClick={addRow}>
                      {intl.formatMessage({ id: "app.socialContentGrid.addRow" })}
                    </Button>
                    <Button
                      color="success"
                      onClick={copyPublicLink}
                      disabled={!hasRows}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.generatePublicLink",
                      })}
                    </Button>
                    <Button
                      color={isPlatformColumnPinned ? "secondary" : "info"}
                      outline
                      className="social-grid-pin-btn rounded-pill"
                      onClick={() => setIsPlatformColumnPinned((prev) => !prev)}
                    >
                      {isPlatformColumnPinned
                        ? intl.formatMessage({
                            id: "app.socialContentGrid.unpinSocialColumn",
                            defaultMessage: "Ocultar columna Red Social",
                          })
                        : intl.formatMessage({
                            id: "app.socialContentGrid.pinSocialColumn",
                            defaultMessage: "Fijar columna Red Social",
                          })}
                    </Button>
                  </div>
                  <Badge color="light" pill className="px-3 py-2 border">
                    {rows.length} items
                  </Badge>
                </CardBody>
              </Card>
            </Col>
          </Row>
          <Row>
            <Col xs="12">
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="social-grid-toolbar d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <Badge color="info" pill className="px-3 py-2">
                    {selectedVisibleRowIds.length}
                    </Badge>
                    <span>
                      {intl.formatMessage({ id: "app.socialContentGrid.selectedRows" })}
                    </span>
                  </div>
                  <Button
                    color="primary"
                    className="rounded-pill"
                    onClick={openBulkCalendarModal}
                    disabled={selectedVisibleRowIds.length === 0}
                  >
                    {intl.formatMessage({
                      id: "app.socialContentGrid.sendSelectedToCalendar",
                    })}
                  </Button>
                </CardBody>
              </Card>
              <Card className="border-0 shadow-sm mb-3">
                <CardBody className="social-grid-toolbar d-flex flex-wrap gap-2 align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <label className="mb-0">
                      {intl.formatMessage({
                        id: "app.socialContentGrid.filterByMonth",
                        defaultMessage: "Filtrar por mes",
                      })}
                    </label>
                    <Input
                      type="month"
                      value={monthFilter}
                      onChange={(e) => setMonthFilter(e.target.value)}
                      onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker?.()}
                      className="rounded"
                    />
                  </div>
                  <div className="d-flex flex-wrap gap-2">
                    <Button
                      color={viewMode === "grid" ? "primary" : "secondary"}
                      outline={viewMode !== "grid"}
                      className="rounded-pill"
                      onClick={() => setViewMode("grid")}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.view.grid",
                        defaultMessage: "Grilla",
                      })}
                    </Button>
                    <Button
                      color={viewMode === "cards" ? "primary" : "secondary"}
                      outline={viewMode !== "cards"}
                      className="rounded-pill"
                      onClick={() => setViewMode("cards")}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.view.cards",
                        defaultMessage: "Cards",
                      })}
                    </Button>
                    <Button
                      color="secondary"
                      outline
                      className="rounded-pill"
                      onClick={() => setMonthFilter("")}
                      disabled={!monthFilter}
                    >
                      {intl.formatMessage({
                        id: "app.socialContentGrid.allMonths",
                        defaultMessage: "Todos los meses",
                      })}
                    </Button>
                  </div>
                </CardBody>
              </Card>
              {viewMode === "grid" ? (
                <Card className="border-0 shadow-sm">
                  <CardBody className="p-0">
                    <div className="table-responsive">
                      <Table hover className="mb-0 public-grid-table social-grid-admin-table">
                        <thead className="bg-light">
                          <tr>
                            <th className="social-grid-select-col">
                              {intl.formatMessage({ id: "app.socialContentGrid.select" })}
                            </th>
                            <th>{intl.formatMessage({ id: "app.socialContentGrid.date" })}</th>
                            {isPlatformColumnPinned && (
                              <th>{intl.formatMessage({ id: "app.socialContentGrid.platform" })}</th>
                            )}
                            <th>{intl.formatMessage({ id: "app.socialContentGrid.format" })}</th>
                            <th>{intl.formatMessage({ id: "app.socialContentGrid.media" })}</th>
                            <th>{intl.formatMessage({ id: "app.socialContentGrid.copy" })}</th>
                            <th>{intl.formatMessage({ id: "app.socialContentGrid.comments" })}</th>
                            <th>{intl.formatMessage({ id: "app.socialContentGrid.status" })}</th>
                            <th>{intl.formatMessage({ id: "app.socialContentGrid.calendar" })}</th>
                            <th>{intl.formatMessage({ id: "app.socialContentGrid.actions" })}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRows.map((row) => (
                            <tr key={row.id}>
                              <td className="social-grid-select-col">
                                <Input
                                  type="checkbox"
                                  className="social-grid-checkbox"
                                  checked={selectedRowIds.includes(row.id)}
                                  onChange={() => toggleRowSelection(row.id)}
                                  disabled={row.scheduledToCalendar}
                                />
                              </td>
                              <td>
                                <Input
                                  type="date"
                                  value={row.date}
                                  onChange={(e) => onChangeRow(row.id, "date", e.target.value)}
                                  className="rounded"
                                />
                              </td>
                              {isPlatformColumnPinned && (
                                <td>
                                  <Input
                                    type="select"
                                    value={row.platform}
                                    onChange={(e) => onChangeRow(row.id, "platform", e.target.value)}
                                    className="rounded"
                                  >
                                    <option value="instagram">Instagram</option>
                                    <option value="facebook">Facebook</option>
                                    <option value="linkedin">LinkedIn</option>
                                    {isTiktokPostingEnabled() ? (
                                      <option value="tiktok">TikTok</option>
                                    ) : null}
                                    <option value="x">X</option>
                                    <option value="youtube">YouTube</option>
                                  </Input>
                                </td>
                              )}
                              <td>
                                <Input
                                  type="select"
                                  value={row.format}
                                  onChange={(e) => onChangeRow(row.id, "format", e.target.value)}
                                  className="rounded"
                                >
                                  <option value="post">Post</option>
                                  <option value="story">Story</option>
                                  <option value="reel">Reel</option>
                                  <option value="video">Video</option>
                                  <option value="carousel">Carousel</option>
                                </Input>
                              </td>
                              <td style={{ minWidth: 220 }}>
                                <Input
                                  type="select"
                                  value={row.mediaType}
                                  onChange={(e) => {
                                    const mediaType = e.target.value as ContentGridRow["mediaType"];
                                    onChangeRow(row.id, "mediaType", mediaType);
                                    if (mediaType === "none") onChangeRow(row.id, "mediaUrl", "");
                                  }}
                                  className="rounded"
                                >
                                  <option value="none">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.none" })}</option>
                                  <option value="image">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.image" })}</option>
                                  <option value="video">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.video" })}</option>
                                  <option value="link">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.link" })}</option>
                                </Input>
                                {row.mediaType === "link" ? (
                                  <Input
                                    className="mt-2 rounded"
                                    value={row.mediaUrl}
                                    placeholder={intl.formatMessage({
                                      id: "app.socialContentGrid.linkPlaceholder",
                                    })}
                                    onChange={(e) =>
                                      onChangeRow(row.id, "mediaUrl", e.target.value)
                                    }
                                  />
                                ) : row.mediaType === "image" ||
                                  row.mediaType === "video" ? (
                                  <Input
                                    className="mt-2 rounded"
                                    type="file"
                                    accept={
                                      row.mediaType === "image" ? "image/*" : "video/*"
                                    }
                                    onChange={async (e) => {
                                      const file = e.target.files?.[0];
                                      if (!file) return;
                                      await onUploadMedia(row.id, file, row.mediaType);
                                    }}
                                  />
                                ) : null}
                                {renderMediaPreview(row)}
                              </td>
                              <td>
                                <Input
                                  type="textarea"
                                  rows={2}
                                  value={row.copy}
                                  onChange={(e) => onChangeRow(row.id, "copy", e.target.value)}
                                  className="rounded"
                                />
                              </td>
                              <td>
                                <Input
                                  type="textarea"
                                  rows={2}
                                  value={row.comments}
                                  onChange={(e) => onChangeRow(row.id, "comments", e.target.value)}
                                  className="rounded"
                                />
                              </td>
                              <td>
                                <Input
                                  type="select"
                                  value={row.status}
                                  onChange={(e) => onChangeRow(row.id, "status", e.target.value)}
                                  className="rounded"
                                >
                                  <option value="pending">{intl.formatMessage({ id: "app.socialContentGrid.status.pending" })}</option>
                                  <option value="approved">{intl.formatMessage({ id: "app.socialContentGrid.status.approved" })}</option>
                                  <option value="rejected">{intl.formatMessage({ id: "app.socialContentGrid.status.rejected" })}</option>
                                </Input>
                              </td>
                              <td>
                                {row.scheduledToCalendar ? (
                                  <Badge color="success" pill className="px-2 py-1">
                                    {row.scheduledNetwork || "-"}
                                  </Badge>
                                ) : (
                                  <Badge color="secondary" pill className="px-2 py-1">
                                    {intl.formatMessage({ id: "app.socialContentGrid.notScheduled" })}
                                  </Badge>
                                )}
                              </td>
                              <td>
                                <div className="social-grid-row-actions d-flex gap-2 flex-wrap">
                                  <Button
                                    color="danger"
                                    outline
                                    className="rounded-pill"
                                    onClick={() => removeRow(row.id)}
                                  >
                                    {intl.formatMessage({ id: "app.socialContentGrid.delete" })}
                                  </Button>
                                  <Button
                                    color="primary"
                                    size="sm"
                                    className="rounded-pill"
                                    onClick={() => openSingleCalendarModal(row.id)}
                                    disabled={row.scheduledToCalendar}
                                  >
                                    {intl.formatMessage({ id: "app.socialContentGrid.sendToCalendar" })}
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </CardBody>
                </Card>
              ) : (
                <div className="social-grid-cards social-grid-cards--cards">
                  {filteredRows.map((row, index) => (
                    <Card key={row.id} className="border-0 shadow-sm social-grid-item-card">
                    <CardHeader className="social-grid-item-card__header d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center gap-2">
                        <Input
                          type="checkbox"
                          className="social-grid-checkbox"
                          checked={selectedRowIds.includes(row.id)}
                          onChange={() => toggleRowSelection(row.id)}
                          disabled={row.scheduledToCalendar}
                        />
                        <strong>{`#${index + 1}`}</strong>
                      </div>
                      {row.scheduledToCalendar ? (
                        <Badge color="success" pill className="px-2 py-1">
                          {row.scheduledNetwork || "-"}
                        </Badge>
                      ) : (
                        <Badge color="secondary" pill className="px-2 py-1">
                          {intl.formatMessage({ id: "app.socialContentGrid.notScheduled" })}
                        </Badge>
                      )}
                    </CardHeader>
                    <CardBody>
                      <div className="social-grid-item-card__body">
                        <div>
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.date" })}</label>
                          <Input
                            type="date"
                            value={row.date}
                            onChange={(e) => onChangeRow(row.id, "date", e.target.value)}
                            className="rounded"
                          />
                        </div>
                        {isPlatformColumnPinned && (
                          <div>
                            <label>{intl.formatMessage({ id: "app.socialContentGrid.platform" })}</label>
                            <Input
                              type="select"
                              value={row.platform}
                              onChange={(e) => onChangeRow(row.id, "platform", e.target.value)}
                              className="rounded"
                            >
                              <option value="instagram">Instagram</option>
                              <option value="facebook">Facebook</option>
                              <option value="linkedin">LinkedIn</option>
                              {isTiktokPostingEnabled() ? (
                                <option value="tiktok">TikTok</option>
                              ) : null}
                              <option value="x">X</option>
                              <option value="youtube">YouTube</option>
                            </Input>
                          </div>
                        )}
                        <div>
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.format" })}</label>
                          <Input
                            type="select"
                            value={row.format}
                            onChange={(e) => onChangeRow(row.id, "format", e.target.value)}
                            className="rounded"
                          >
                            <option value="post">Post</option>
                            <option value="story">Story</option>
                            <option value="reel">Reel</option>
                            <option value="video">Video</option>
                            <option value="carousel">Carousel</option>
                          </Input>
                        </div>
                        <div className="social-grid-card-field--wide">
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.media" })}</label>
                          <Input
                            type="select"
                            value={row.mediaType}
                            onChange={(e) => {
                              const mediaType = e.target.value as ContentGridRow["mediaType"];
                              onChangeRow(row.id, "mediaType", mediaType);
                              if (mediaType === "none") {
                                onChangeRow(row.id, "mediaUrl", "");
                              }
                            }}
                            className="rounded"
                          >
                            <option value="none">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.none" })}</option>
                            <option value="image">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.image" })}</option>
                            <option value="video">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.video" })}</option>
                            <option value="link">{intl.formatMessage({ id: "app.socialContentGrid.mediaType.link" })}</option>
                          </Input>
                          {row.mediaType === "link" ? (
                            <Input
                              className="mt-2 rounded"
                              value={row.mediaUrl}
                              placeholder={intl.formatMessage({
                                id: "app.socialContentGrid.linkPlaceholder",
                              })}
                              onChange={(e) => onChangeRow(row.id, "mediaUrl", e.target.value)}
                            />
                          ) : row.mediaType === "image" || row.mediaType === "video" ? (
                            <Input
                              className="mt-2 rounded"
                              type="file"
                              accept={row.mediaType === "image" ? "image/*" : "video/*"}
                              onChange={async (e) => {
                                const file = e.target.files?.[0];
                                if (!file) return;
                                await onUploadMedia(row.id, file, row.mediaType);
                              }}
                            />
                          ) : null}
                          {renderMediaPreview(row)}
                        </div>
                        <div className="social-grid-card-field--wide">
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.copy" })}</label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={row.copy}
                            onChange={(e) => onChangeRow(row.id, "copy", e.target.value)}
                            className="rounded"
                          />
                        </div>
                        <div className="social-grid-card-field--wide">
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.comments" })}</label>
                          <Input
                            type="textarea"
                            rows={3}
                            value={row.comments}
                            onChange={(e) => onChangeRow(row.id, "comments", e.target.value)}
                            className="rounded"
                          />
                        </div>
                        <div>
                          <label>{intl.formatMessage({ id: "app.socialContentGrid.status" })}</label>
                          <Input
                            type="select"
                            value={row.status}
                            onChange={(e) => onChangeRow(row.id, "status", e.target.value)}
                            className="rounded"
                          >
                            <option value="pending">{intl.formatMessage({ id: "app.socialContentGrid.status.pending" })}</option>
                            <option value="approved">{intl.formatMessage({ id: "app.socialContentGrid.status.approved" })}</option>
                            <option value="rejected">{intl.formatMessage({ id: "app.socialContentGrid.status.rejected" })}</option>
                          </Input>
                        </div>
                      </div>
                      <div className="social-grid-row-actions d-flex gap-2 flex-wrap mt-3">
                        <Button
                          color="danger"
                          outline
                          className="rounded-pill"
                          onClick={() => removeRow(row.id)}
                        >
                          {intl.formatMessage({ id: "app.socialContentGrid.delete" })}
                        </Button>
                        <Button
                          color="primary"
                          size="sm"
                          className="rounded-pill"
                          onClick={() => openSingleCalendarModal(row.id)}
                          disabled={row.scheduledToCalendar}
                        >
                          {intl.formatMessage({
                            id: "app.socialContentGrid.sendToCalendar",
                          })}
                        </Button>
                      </div>
                    </CardBody>
                  </Card>
                  ))}
                </div>
              )}
              {filteredRows.length === 0 && (
                <Card className="border-0 shadow-sm mt-3">
                  <CardBody className="text-center py-4">
                    {intl.formatMessage({
                      id: "app.socialContentGrid.emptyFiltered",
                      defaultMessage: "No hay contenidos para el mes seleccionado.",
                    })}
                  </CardBody>
                </Card>
              )}
            </Col>
          </Row>
          <Modal
            isOpen={Boolean(calendarModal)}
            toggle={() => setCalendarModal(null)}
            className="social-grid-calendar-modal"
            contentClassName="social-grid-calendar-modal__content"
            centered
          >
            <ModalHeader toggle={() => setCalendarModal(null)}>
              {intl.formatMessage({ id: "app.socialContentGrid.calendarModalTitle" })}
            </ModalHeader>
            <ModalBody>
              <p className="mb-3 social-grid-calendar-modal__description">
                {intl.formatMessage({
                  id:
                    calendarModal?.mode === "bulk"
                      ? "app.socialContentGrid.calendarBulkDescription"
                      : "app.socialContentGrid.calendarSingleDescription",
                })}
              </p>
              <label className="mb-2 d-block font-weight-bold">
                {intl.formatMessage({
                  id: "app.socialContentGrid.selectNetwork",
                  defaultMessage: "Selecciona la red social destino",
                })}
              </label>
              {availableCalendarNetworks.length > 0 ? (
                <Input
                  type="select"
                  value={calendarNetwork}
                  onChange={(e) => setCalendarNetwork(e.target.value)}
                  className="rounded"
                >
                  {availableCalendarNetworks.map((network) => (
                    <option key={network.value} value={network.value}>
                      {network.label}
                    </option>
                  ))}
                </Input>
              ) : (
                <div>
                  <p className="mb-2 text-warning">
                    {intl.formatMessage({
                      id: "app.socialContentGrid.noSyncedNetworks",
                      defaultMessage:
                        "No hay redes sociales sincronizadas para enviar al calendario.",
                    })}
                  </p>
                  <Button color="info" outline onClick={goToSocialManager}>
                    {intl.formatMessage({
                      id: "app.socialContentGrid.syncNetworksCta",
                      defaultMessage: "Ir a Redes Sociales para sincronizar",
                    })}
                  </Button>
                  <p className="mt-2 mb-0 text-muted">
                    {intl.formatMessage({
                      id: "app.socialContentGrid.syncNetworksHint",
                      defaultMessage:
                        "Necesitas al menos una red conectada para programar contenidos en el calendario.",
                    })}
                  </p>
                </div>
              )}
            </ModalBody>
            <ModalFooter className="social-grid-calendar-modal__footer">
              <Button color="secondary" onClick={() => setCalendarModal(null)}>
                {intl.formatMessage({ id: "app.socialContentGrid.cancel" })}
              </Button>
              <Button
                color="primary"
                onClick={() => {
                  if (!calendarNetwork) return;
                  const targetIds = getTargetIdsFromModal();
                  if (targetIds.length === 0) return;
                  setCalendarConfirmModal({
                    targetIds,
                    network: calendarNetwork,
                  });
                }}
                disabled={availableCalendarNetworks.length === 0 || !calendarNetwork}
              >
                {intl.formatMessage({ id: "app.socialContentGrid.confirmSendCalendar" })}
              </Button>
            </ModalFooter>
          </Modal>
          <Modal
            isOpen={Boolean(calendarConfirmModal)}
            toggle={() => setCalendarConfirmModal(null)}
            className="social-grid-calendar-modal"
            contentClassName="social-grid-calendar-modal__content"
            centered
          >
            <ModalHeader toggle={() => setCalendarConfirmModal(null)}>
              {intl.formatMessage({
                id: "app.socialContentGrid.confirmDecisionTitle",
                defaultMessage: "Confirmar decisión",
              })}
            </ModalHeader>
            <ModalBody>
              {intl.formatMessage(
                {
                  id: "app.socialContentGrid.confirmCalendarDispatchDescription",
                  defaultMessage:
                    "Vas a enviar {count} publicaciones al calendario para {network}. ¿Deseas continuar?",
                },
                {
                  count: calendarConfirmModal?.targetIds.length ?? 0,
                  network: getNetworkLabel(
                    calendarConfirmModal?.network ?? calendarNetwork
                  ),
                }
              )}
            </ModalBody>
            <ModalFooter className="social-grid-calendar-modal__footer">
              <Button
                color="secondary"
                onClick={() => setCalendarConfirmModal(null)}
                disabled={isSendingCalendar}
              >
                {intl.formatMessage({ id: "app.socialContentGrid.cancel" })}
              </Button>
              <Button
                color="primary"
                onClick={confirmSendToCalendar}
                disabled={isSendingCalendar}
              >
                {isSendingCalendar
                  ? intl.formatMessage({
                      id: "app.asiggnUser.searching",
                      defaultMessage: "Procesando...",
                    })
                  : intl.formatMessage({
                      id: "app.socialContentGrid.confirmAndSend",
                    })}
              </Button>
            </ModalFooter>
          </Modal>
          <Modal
            isOpen={Boolean(calendarResultMessage)}
            toggle={() => setCalendarResultMessage(null)}
            centered
          >
            <ModalHeader toggle={() => setCalendarResultMessage(null)}>
              {intl.formatMessage({
                id: "app.socialContentGrid.calendarModalTitle",
                defaultMessage: "Calendario",
              })}
            </ModalHeader>
            <ModalBody>{calendarResultMessage}</ModalBody>
            <ModalFooter>
              <Button color="primary" onClick={() => setCalendarResultMessage(null)}>
                {intl.formatMessage({
                  id: "app.socialContentGrid.accept",
                  defaultMessage: "Aceptar",
                })}
              </Button>
            </ModalFooter>
          </Modal>
          <Modal
            isOpen={Boolean(publicLinkMessage)}
            toggle={() => setPublicLinkMessage(null)}
            centered
          >
            <ModalHeader toggle={() => setPublicLinkMessage(null)}>
              {intl.formatMessage({
                id: "app.socialContentGrid.publicLinkModalTitle",
                defaultMessage: "Link publico",
              })}
            </ModalHeader>
            <ModalBody>
              {publicLinkMessage?.text}
            </ModalBody>
            <ModalFooter>
              <Button
                color={publicLinkMessage?.type === "success" ? "success" : "secondary"}
                onClick={() => setPublicLinkMessage(null)}
              >
                {intl.formatMessage({
                  id: "app.socialContentGrid.accept",
                  defaultMessage: "Aceptar",
                })}
              </Button>
            </ModalFooter>
          </Modal>
        </Container>
      </Content>
    </Layout>
  );
}
